// Shared-with-me forecast load. Hydrates into the separate `sharedRunsById` slice, never
// `runsById`, because branch/conversation root-walks assume ownership (see reducer.ts's
// `RunState.sharedRunsById`).
//
// Rows load lean, like the owner leg: cards draw from the lean summary, and a shared forecast's
// detail is read by id when it is opened, or when it has a newer forecast to promote
// (commands/loadForecastDetail.ts), which is also where the "newest reforecast becomes live"
// overlay runs. A forecast already filled keeps its detail through a reload of this list, so
// its newer forecast is promoted here instead, and a newer saved update is re-read by id.
import { hydrateRows, rowRunId } from "../lib/forecast/hydrate.js";
import { sharedHydrated } from "../lib/forecast/reducer.js";
import { sharedLiveVintages } from "../lib/forecast/sharedLiveVintages.js";
import type { CommandDeps } from "./deps.js";
import { applyNewestReforecastAll } from "./loadForecasts.js";
import { refreshDetail, savedVintageChanged } from "./loadForecastDetail.js";

export interface LoadSharedForecastsResult {
  ok: boolean;
  nextPageToken: string | null;
  /**
   * Returned rather than applied here because the pin map is `App`'s `useState`, not
   * store state, and this command has no `ui` dep (unlike `bootForecasts`).
   */
  pinned: Record<string, unknown>;
  /** The run ids this page hydrated (empty when nothing was). */
  runIds: string[];
  /**
   * The load was superseded (`cancelled`) before its response arrived. Nothing was
   * hydrated, and the caller must not apply this result to its pagination state either:
   * a newer load owns it. `ok` is false alongside it, so a caller that ignores `stale`
   * still does not advance a cursor from a page it never merged.
   */
  stale?: true;
}

/**
 * `cancelled` reports that this load has been superseded (a reload, an unmount). The filled
 * runs' vintage overlay checks it again before dispatching, after its report fetches.
 */
export async function loadSharedForecasts(
  deps: CommandDeps,
  { cursor, cancelled }: { cursor?: string | null; cancelled?: () => boolean } = {},
): Promise<LoadSharedForecastsResult> {
  const { store } = deps;
  const r = await deps.services.api.listSharedForecasts({ view: "lean", cursor });
  if (cancelled?.()) return { ok: false, stale: true, nextPageToken: null, pinned: {}, runIds: [] };
  if (!r.ok) return { ok: false, nextPageToken: null, pinned: {}, runIds: [] };
  // hydrateRows resolves root/branch topology within this page's own row set and touches no
  // other state, so it is safe to call independently of the owner leg's hydrateRows call.
  // hydrateRows lives in a still-JS module; its inferred empty-object return doesn't satisfy
  // the typed Record<string, Run> parameter, so the call casts at the boundary.
  const { runsById } = hydrateRows(r.forecasts as any[], {}, { lean: true });
  // Forecasts already filled that changed on the server since (a newer save, a finished run):
  // decided against the runs as they were, since the hydrate replaces their status.
  const prior = store.getState().sharedRunsById;
  const incoming = runsById as Record<string, any>;
  const stale = new Set(
    Object.keys(incoming).filter((id) =>
      savedVintageChanged(prior[id], incoming[id], { resolutionLands: true }),
    ),
  );
  store.dispatch(sharedHydrated(runsById as any) as any);
  // Re-read by id, which also promotes from the fresh row; the rest promote from what they hold.
  if (stale.size) void refreshDetail(deps, [...stale], cancelled);
  if (sharedLiveVintages()) {
    const rows = r.forecasts.filter((row: any) => !stale.has(rowRunId(row) ?? ""));
    applyNewestReforecastAll(filledRows(deps, rows), cancelled);
  }
  return {
    ok: true,
    nextPageToken: r.nextPageToken,
    pinned: r.pinned,
    runIds: Object.keys(runsById as any),
  };
}

/**
 * This page's rows whose runs already hold their detail (opened earlier), each carrying the run's
 * own history and the pointers the hydrate merged: the full rows the vintage overlay needs. A
 * lean row's history is empty, so its live moment would read as its creation.
 */
function filledRows(deps: CommandDeps, rows: any[]): any[] {
  const shared = deps.store.getState().sharedRunsById;
  const out: any[] = [];
  for (const row of rows) {
    const run = shared[rowRunId(row) ?? ""];
    if (!run || run.detailPending) continue;
    out.push({ ...row, history: run.history, reportPointers: run.reportPointers });
  }
  return out;
}
